import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const STORED_URL_KEY = 'lsgo.chunk_recovery.url';
const LAST_RELOAD_TS_KEY = 'lsgo.chunk_recovery.last_reload_ts';
const COOLDOWN_MS = 30 * 1000;

const CHUNK_ERROR_PATTERNS = [
    /ChunkLoadError/i,
    /Loading chunk [0-9]+ failed/i,
    /dynamically imported module/i,
    /Failed to fetch dynamically imported module/i,
];

const now = () => Date.now();

const getMessageFromUnknown = value => {
    if (!value) return '';
    if (typeof value === 'string') return value;
    if (value instanceof Error) return value.message || '';
    if (typeof value.message === 'string') return value.message;
    if (typeof value.reason === 'string') return value.reason;
    if (value.reason && typeof value.reason.message === 'string') return value.reason.message;
    return '';
};

const isChunkLoadFailure = value => {
    const message = getMessageFromUnknown(value);
    return CHUNK_ERROR_PATTERNS.some(pattern => pattern.test(message));
};

const logDiagnostic = (action, details = {}) => {
    if (!window?.newrelic) return;
    const payload = {
        action,
        ...details,
        href: window.location.href,
    };

    if (typeof window.newrelic.addPageAction === 'function') {
        window.newrelic.addPageAction('ChunkRecovery', payload);
        return;
    }

    if (typeof window.newrelic.noticeError === 'function') {
        window.newrelic.noticeError(new Error(`ChunkRecovery:${action}`), payload);
    }
};

const safeSessionGet = key => {
    try {
        return sessionStorage.getItem(key);
    } catch (error) {
        return null;
    }
};

const safeSessionSet = (key, value) => {
    try {
        sessionStorage.setItem(key, value);
    } catch (error) {
        // Ignore storage failures so app can continue.
    }
};

const safeSessionRemove = key => {
    try {
        sessionStorage.removeItem(key);
    } catch (error) {
        // Ignore storage failures so app can continue.
    }
};

const triggerRecoveryReload = source => {
    const lastReloadTsRaw = safeSessionGet(LAST_RELOAD_TS_KEY);
    const lastReloadTs = Number(lastReloadTsRaw || 0);
    const inCooldown = now() - lastReloadTs < COOLDOWN_MS;

    if (inCooldown) {
        logDiagnostic('skip_reload', {
            source,
            inCooldown,
        });
        return false;
    }

    safeSessionSet(STORED_URL_KEY, window.location.href);
    safeSessionSet(LAST_RELOAD_TS_KEY, String(now()));
    logDiagnostic('reload', { source });
    window.location.reload();
    return true;
};

export const installChunkLoadRecovery = () => {
    const handleWindowError = event => {
        const candidate = event?.error || event?.message;
        if (!isChunkLoadFailure(candidate)) return;
        const didReload = triggerRecoveryReload('window_error');
        if (didReload && typeof event.preventDefault === 'function') {
            event.preventDefault();
        }
    };

    const handleUnhandledRejection = event => {
        const candidate = event?.reason || event;
        if (!isChunkLoadFailure(candidate)) return;
        const didReload = triggerRecoveryReload('unhandledrejection');
        if (didReload && typeof event.preventDefault === 'function') {
            event.preventDefault();
        }
    };

    window.addEventListener('error', handleWindowError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
};

export const ChunkRecoveryUrlRestorer = () => {
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        const storedUrl = safeSessionGet(STORED_URL_KEY);
        if (!storedUrl) return;

        let targetPath = '';
        try {
            const target = new URL(storedUrl, window.location.origin);
            targetPath = `${target.pathname}${target.search}${target.hash}`;
        } catch (error) {
            safeSessionRemove(STORED_URL_KEY);
            logDiagnostic('restore_failed_parse');
            return;
        }

        const currentPath = `${location.pathname}${location.search}${location.hash}`;
        if (targetPath && targetPath !== currentPath) {
            navigate(targetPath, { replace: true });
            logDiagnostic('restore_navigate', { targetPath });
        } else if (targetPath && window.history?.replaceState) {
            window.history.replaceState(window.history.state, '', targetPath);
            logDiagnostic('restore_replace_state', { targetPath });
        }

        safeSessionRemove(STORED_URL_KEY);
    }, [location.hash, location.pathname, location.search, navigate]);

    return null;
};
